import React, { useState, useEffect } from 'react';
import { Box, Container, TextField, Button, Typography, Paper, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { signIn, fetchAuthSession } from 'aws-amplify/auth';
import { RootState } from '../../store/store';
import { setUserId as setAmplitudeUserId, track as trackAmplitude } from '../../services/amplitude';
import {
  setAuthentication,
  setIsAdmin,
  setAuthView,
  clearLoginMessage,
  setLoading
} from '../../store/appSlice';
import carbonSpaceLogo from '../../assets/logo.png';
import mapSvg from '../../assets/map.svg';

interface LoginViewProps {
    onNewPasswordRequired: (user: any) => void;
  }

const LoginContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  backgroundColor: theme.palette.background.default,
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  borderRadius: (theme.shape.borderRadius as number) * 2,
  overflow: 'hidden',
  boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.1)',
  maxWidth: 960,
  width: '100%',
}));

const FormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '50%',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: theme.spacing(4),
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  width: '50%',
  backgroundColor: theme.palette.accent.lightBlue,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.primary,
  padding: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const LogoImage = styled('img')(({ theme }) => ({
  width: 150,
  marginBottom: theme.spacing(2),
}));

const MapImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '100%',
});

const LoginView: React.FC<LoginViewProps> = ({ onNewPasswordRequired }) => {
  const dispatch = useDispatch();
  const { loginMessage } = useSelector((state: RootState) => state.app);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Clear the message after it's been displayed
    if (loginMessage) {
      setTimeout(() => {
        dispatch(clearLoginMessage());
      }, 5000); // clear after 5 seconds
    }
  }, [loginMessage, dispatch]);

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    dispatch(setLoading(true));
    setError('');

    try {
      const { isSignedIn, nextStep } = await signIn({ username: email, password: password });

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // This is where you'd typically handle the new password required flow.
        // For example, you could set some state to show a "new password" form.
        console.log('User needs to set a new password');
        onNewPasswordRequired(nextStep);

      } else if (isSignedIn) {
        // Check if user is admin from token
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        const userIsAdmin = idToken?.payload['custom:isAdmin'] === 'true';

        console.log('Login successful - userIsAdmin:', userIsAdmin);
        console.log('ID Token payload:', idToken?.payload);

        dispatch(setIsAdmin(userIsAdmin));
        dispatch(setAuthentication(true));

        // Set Amplitude user ID from email and track login event
        const userEmail = idToken?.payload.email;
        if (userEmail && typeof userEmail === 'string') {
          setAmplitudeUserId(userEmail);
          trackAmplitude('User Logged In', {
            isAdmin: userIsAdmin,
          });
        }

        // For admin users, they'll be redirected to AccountSelectionView
        // Regular users proceed directly to the dashboard
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  const handleForgotPassword = () => {
    dispatch(setAuthView('forgot-password'));
  };

  return (
    <LoginContainer maxWidth={false}>
      <LoginPaper>
        <FormSection>
          <LogoImage src={carbonSpaceLogo} alt="CarbonSpace Logo" />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }} gutterBottom>
            Welcome Back
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={4}>
            Sign in to continue to CarbonSpace
          </Typography>

          {loginMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {loginMessage}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <TextField
            label="Password"
            type="password"
            variant="outlined"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
          />

          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2, py: 1.5, textTransform: 'none', fontSize: '1rem' }}
            onClick={handleLogin}
          >
            Sign In
          </Button>

          <Button
            variant="text"
            fullWidth
            sx={{ mt: 1, textTransform: 'none' }}
            onClick={handleForgotPassword}
          >
            Forgot Password?
          </Button>
        </FormSection>
        <ImageSection>
          <MapImage src={mapSvg} alt="Map Illustration" />
        </ImageSection>
      </LoginPaper>
    </LoginContainer>
  );
};

export default LoginView;
